import type { Breadcrumb, Event } from '@sentry/react'

/*
  Invitation IDs are bearer credentials: whoever holds one can register a root-admin or
  tenant-admin passkey. They appear in the accept-invite page URL (`?inviteId=…`), in the
  challenge request (`?invitation-id=…`) and in the invitation lookup path
  (`/…/auth/invitation/<id>`). Sentry records those URLs in error and transaction events, their
  breadcrumbs and spans, and Session Replay's breadcrumbs, performance spans and `urls`, so their
  values are taken out, plain, URL-encoded or encoded twice (a redirect nested in a redirect).
*/
export const REDACTED = '[redacted]'

const INVITE_ID_PARAM = '(?:inviteId|invitationId|invitation-id|invitation_id)'
const EQUALS = '(?:=|%3D|%253D)'
const SLASH = '(?:/|%2F|%252F)'
// A value ends at the next parameter or fragment, encoded or not, so the parameters after it survive.
const VALUE = `(?:(?!%26|%2526|%23|%2523)[^&#/?\\s"'<>])+`

const INVITE_ID_PARAM_IN_TEXT = new RegExp(`(${INVITE_ID_PARAM}${EQUALS})${VALUE}`, 'gi')
const INVITE_ID_PATH_IN_TEXT = new RegExp(`(${SLASH}invitation${SLASH})${VALUE}`, 'gi')
const INVITE_ID_IN_URL = new RegExp(`${INVITE_ID_PARAM}${EQUALS}|${SLASH}invitation${SLASH}`, 'i')

export const redactInviteIds = (text: string): string =>
  text.replace(INVITE_ID_PARAM_IN_TEXT, `$1${REDACTED}`).replace(INVITE_ID_PATH_IN_TEXT, `$1${REDACTED}`)

export const hasInviteId = (url: string): boolean => INVITE_ID_IN_URL.test(url)

const MAX_REDACT_DEPTH = 8

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

// Returns the same reference when nothing needed redacting, so untouched data is not copied.
export const redactInviteIdsDeep = (value: unknown, depth = 0): unknown => {
  if (typeof value === 'string') return redactInviteIds(value)
  if (depth >= MAX_REDACT_DEPTH) return value
  if (Array.isArray(value)) {
    let changed = false
    const next = value.map((item) => {
      const redacted = redactInviteIdsDeep(item, depth + 1)
      if (redacted !== item) changed = true
      return redacted
    })
    return changed ? next : value
  }
  if (isPlainObject(value)) {
    let changed = false
    const next: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) {
      const redacted = redactInviteIdsDeep(item, depth + 1)
      if (redacted !== item) changed = true
      next[key] = redacted
    }
    return changed ? next : value
  }
  return value
}

// Session Replay `beforeAddRecordingEvent` hook: strips invite IDs from recorded custom events
// (breadcrumbs and performance spans with their URLs).
export const redactRecordingEvent = <T>(event: T): T => redactInviteIdsDeep(event) as T

// Sentry `beforeBreadcrumb` hook. Navigation (`from`/`to`) and fetch/xhr (`url`) breadcrumbs carry
// the page and request URLs; Session Replay records the breadcrumb this hook returns.
export const redactBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => ({
  ...breadcrumb,
  ...(breadcrumb.message !== undefined ? { message: redactInviteIds(breadcrumb.message) } : {}),
  ...(breadcrumb.data !== undefined ? { data: redactInviteIdsDeep(breadcrumb.data) as Breadcrumb['data'] } : {}),
})

// Strips invite IDs from the parts of any event (error, transaction, replay) that carry a URL:
// `request`, `transaction`, `tags`, the replay `urls`, transaction `spans`, `breadcrumbs`,
// `extra` and `contexts`.
export const redactInviteIdsInEvent = <T extends Event>(event: T): T => {
  if (event.message !== undefined) event.message = redactInviteIds(event.message)
  event.exception?.values?.forEach((exception) => {
    if (exception.value !== undefined) exception.value = redactInviteIds(exception.value)
  })
  if (event.request) event.request = redactInviteIdsDeep(event.request) as Event['request']
  if (event.transaction !== undefined) event.transaction = redactInviteIds(event.transaction)
  if (event.tags) event.tags = redactInviteIdsDeep(event.tags) as Event['tags']
  const withUrls = event as T & { urls?: unknown }
  if (Array.isArray(withUrls.urls)) withUrls.urls = redactInviteIdsDeep(withUrls.urls)
  if (event.spans) event.spans = redactInviteIdsDeep(event.spans) as Event['spans']
  if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(redactBreadcrumb)
  if (event.extra) event.extra = redactInviteIdsDeep(event.extra) as Event['extra']
  if (event.contexts) event.contexts = redactInviteIdsDeep(event.contexts) as Event['contexts']
  return event
}

// Sentry `beforeSendTransaction` hook: page-load and navigation transactions, and their
// http.client spans, carry the invite URLs.
export const redactTransaction = <T extends Event>(event: T): T => redactInviteIdsInEvent(event)
